import { newInstance, autoinject, singleton, bindable, bindingMode, observable } from 'aurelia-framework';
import { TaskActionService } from '../TaskActionService';
import { UiActionButton } from '../UiActionButton';
import { PageMessageService } from '../../../services/page-message-service';
import { BusyService } from '../../../services/busy-service';
import { DialogService } from 'aurelia-dialog';
import { WebApiService } from '../../../services/web-api-service';
import { SimpleDialog } from '../../dialogs/simple-dialog';
import { UpdateUserDialog } from '../../dialogs/update-user-dialog';
import { Guid } from 'guid-typescript';
import { SecurityService } from '../../../services/security-service';
import { CmpTaskDto } from '../../../data/typegen/dtos/cmp-task-dto';
import { TaskState } from '../../../data/enums/taskState';
import { UpdateUserModel } from '../../../data/typegen/dtos/update-user-model';
import { ProcessNewUserTaskDto } from '../../../data/typegen/dtos/process-new-user-task-dto';
import { CmpTaskCommentInsertDto } from '../../../data/dtos/cmp-task-comment-insert-dto';
import { ValidationController } from 'aurelia-validation';
import { NewUserRequestVariablesDto } from '../../../data/typegen/dtos/new-user-request-variables-dto';
import { DoUiActionDto } from '../../../data/typegen/dtos/do-ui-action-dto';
import { CmpTaskBaseDto } from '../../../data/typegen/dtos/cmp-task-base-dto';
import { NewUserRequestTaskDto } from '../../../data/typegen/dtos/new-user-request-task-dto';
import { DoUiActionClientHandlerResult } from '../../../data/dtos/do-ui-action-client-handler-result';
import { RequestDto } from '../../../data/typegen/dtos/request-dto';
import { ComposeEmailDialog } from '../../dialogs/compose-email-dialog/compose-email-dialog';


export interface IFulfillmentWidget {
    initUi();
    validate(controller: ValidationController, obj: any): Promise<boolean>;
    doAction(action: DoUiActionDto<CmpTaskBaseDto>): Promise<DoUiActionClientHandlerResult>;
    afterActionSubmit(uiActionKey: string, error?: any): Promise<boolean>;
    showCloseAction: boolean;
    additionalData?: any;
}

export class FulfillmentWidget{
    constructor(protected element:Element){

    }

    
    fireOnreadyEvent() {
        let evt;
        if (window["CustomEvent"]) {
            evt = new CustomEvent('onready', {
                detail: {
                    //value: (<any>event.target).value
                    value: undefined
                },
                bubbles: true
            });
        } else {
            evt = document.createEvent('CustomEvent');
            evt.initCustomEvent('onready', true, true, {
                detail: {
                    //value: (<any>event.target).value
                    value: undefined
                }
            });
        }
        (<any>this.element).dispatchEvent(evt);
    }
}

@autoinject()
export class NewUserRequestFulfillment extends FulfillmentWidget implements IFulfillmentWidget {

    public showCloseAction: boolean = false;
    public isSsoUser: boolean = undefined;


    async validate(controller: ValidationController, obj: any) {
        return true;
    }

    private rejectAction: UiActionButton
    private approveAction: UiActionButton

    public isComponentReadOnly = false;

    @bindable({ defaultBindingMode: bindingMode.oneWay }) readonly: boolean;

    @bindable({ defaultBindingMode: bindingMode.oneWay }) task: CmpTaskDto;
    taskChanged() {
        this.initUi();
    }

    // NOTE: A user could start with "%waad" and be SSO for another applicaiton, and username-password for CMP
    private isSsoUserId(auth0UserId: string): boolean {
        var answer = !!auth0UserId && (auth0UserId.toLowerCase().indexOf("waad|") > -1);
        return answer;
    }


    constructor(
        private taskActionService: TaskActionService,
        private busyService: BusyService,
        private securityService: SecurityService,
        private msg: PageMessageService,
        private dialogService: DialogService,
        private api: WebApiService, 
        element:Element
    ) {
        super(element);
        this.rejectAction = this.taskActionService.addUiAction({
            key: "reject",
            name: "Reject"
        });
        this.approveAction = this.taskActionService.addUiAction({
            key: "approve",
            name: "Approve"
        });
    }


    async afterActionSubmit(uiAction: string, error?: any): Promise<boolean> {
        // return true to reload the data
        return true;
    }

    public async showUserDialog(userId) {
        await this.dialogService.open(
            {
                viewModel: UpdateUserDialog,
                model: {
                    user: { id: userId },
                    skipDbUpdate: false
                }
            });
    }

    public async showEmailDialog(email: string) {
        var result = await this.dialogService.open(
            {
                viewModel: ComposeEmailDialog,
                model: {
                    recipients: email,
                    targetRowId: this.task.taskId,
                    targetTable: this.task.taskClass.toString().toLowerCase()
                }
            });

    }
    public async initUi() {

        var isEditor = this.securityService.hasRole("Edit");
        this.isComponentReadOnly = this.readonly || !this.task.active || !isEditor;

        this.isSsoUser = this.isSsoUserId(this.task.variables.Auth0UserId);
        //only show UI actions when task is assigned and active
        this.approveAction.visible = this.task.active == true && this.task.assignedTo.value == this.securityService.user.id;
        this.rejectAction.visible = this.task.active == true && this.task.assignedTo.value == this.securityService.user.id;
    }


    public async doAction(action: DoUiActionDto<CmpTaskBaseDto>): Promise<DoUiActionClientHandlerResult> {

        if (action.uiActionVerb == this.rejectAction.key) {
            if (!this.task.variables.DenyReason) {
                this.msg.setErrorMessage("Deny Reason is required");
                return DoUiActionClientHandlerResult.stop;
                ; // return null if you want nothing else to happen
            }
        }
        if (action.uiActionVerb == this.approveAction.key) {
            var updateUserModel = await this.getUserUpdateModelAsync();
            if (updateUserModel) {
                if (updateUserModel) {
                    var dto = new ProcessNewUserTaskDto();
                    dto.updateUserModel = updateUserModel;
                    dto.newUserRequestTaskDto = this.task;
                    await this.processNewUserTask(dto);
                    return { reload: true , performValidation: true};
                }
            }
        }
        return DoUiActionClientHandlerResult.default;
    }

    private async getUserUpdateModelAsync(): Promise<UpdateUserModel> {

        this.msg.clear();
        var userModel = new UpdateUserModel();
        //var userTaskVariables = this.task.variables as NewUserRequestVariablesDto;
        userModel.email = this.task.variables.EmailAddress;
        userModel.firstName = this.task.variables.FirstName;
        userModel.lastName = this.task.variables.LastName;
        userModel.userName = this.task.variables.EmailAddress;
        userModel.auth0UserId = this.task.variables.Auth0UserId;

        const userUpdate: UpdateUserModel = await this.finalizeUserInfo(userModel);
        return userUpdate;
    }

    private async processNewUserTask(dto: ProcessNewUserTaskDto) {
        try {
            var responseMessage = await this.api.post("auth", "ProcessNewUserTask", dto);
            var requestDto: RequestDto = JSON.parse(responseMessage.response || "null") as RequestDto;
        }
        catch (e) {
            this.msg.setErrorMessage("Error occurred while creating new user account: " + (e.message || e.errorMessage || JSON.stringify(e)));
            return null;
        }
    }

    async finalizeUserInfo(user: UpdateUserModel): Promise<UpdateUserModel> {
        // if existing local CMP account exists (legacy account prior auth0 implmentation)
        // then we want to use that one, instead of creating a new one
        if (!user.id) {

            // try to get CMP ID for this email, if it exists.
            this.busyService.on();
            try {
                var responseMessage = await this.api.get("Auth", "GetCmpUserByEmail", { emailAddress: user.email });
                this.busyService.off();
                var existingUser = JSON.parse(responseMessage.response || "null");
                if (existingUser) {
                    user.id = existingUser.id;
                    await this.dialogService.open({
                        viewModel: SimpleDialog,
                        model: {
                            title: "This user already has an existing CMP user account.",
                            icon: "fa-info-circle",
                            message: `Please review the user's settings for accuracy.`,
                            buttons: ["Ok"]
                        }
                    })
                        .whenClosed((result) => {
                            return true;
                        });
                }

            } catch (e) {
                this.busyService.off();
                alert("Error check for existing user: " + e);
            }
        }
        return new Promise((resolve) => {
            this.dialogService.open(
                {
                    viewModel: UpdateUserDialog,
                    model: {
                        user: user,
                        skipDbUpdate: true,
                        ensureViewRole: true
                    }
                })
                .whenClosed((result) => {
                    if (!result.wasCancelled) {
                        resolve(result.output);
                    } else {
                        resolve(null);
                    }
                });
        });
    }

    async attached() {
        await this.initUi();
        await this.fireOnreadyEvent();
    }

}